import { Theme, ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from 'src/UIKit/theme';
import React, { FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { UpdateNotification } from "react-update-popup";
import "react-update-popup/dist/index.css";
import "./index.css";
import router from "src/routes/Routes";
import { AuthContext } from "./contexts/AuthContext";
import { hideAppBootSpinner } from "src/helpers/helpers";
import { getApplicationSettings } from "./modules/applications/slices";
import { ApplicationSettings } from "./modules/applications/types";
import ApplicationErrorBoundary from "./components/ApplicationErrorBoundary";
import { Box } from "@mui/material";
// Imported by its own path rather than through the signup barrel: that barrel reaches `AuthContext`,
// which reaches the signup helpers, and the cycle leaves one of them undefined at module init.
import { SignupStepUnavailable } from "src/modules/signup/components/SignupStepUnavailable";
import { StartJoinCampaignDialogHost } from "src/modules/campaigns/components/StartJoinCampaignDialog";

function App() {
  const authContext = useContext(AuthContext);
  const settings = useSelector(getApplicationSettings);

  /**
   * Startup could not find out whether this company belongs in the funnel, so nothing of the app is rendered:
   * the router would place an unfinished signup in the ordinary product on a question nobody answered. The
   * settings are already loaded by this point — the placement check runs after them — so the retry is themed.
   */
  if (authContext?.signupStateUnavailable) {
    return (
      <CustomThemeProvider settings={settings}>
        <Box py={8} px={2}>
          <SignupStepUnavailable onRetry={authContext.retryStartup} />
        </Box>
      </CustomThemeProvider>
    );
  }

  if (!authContext?.isReady) return null;

  return (
    <>
      {settings?.name && <Helmet defaultTitle={settings.name} />}

      {settings?.favicon && (
        <Helmet>
          <link rel="icon" href={settings.favicon} sizes="16x16" />{" "}
        </Helmet>
      )}

      <CustomThemeProvider settings={settings}>
        <RouterProvider router={router} />
        <ApplicationErrorBoundary context="start-join-campaign-dialog" fallback={null}>
          <StartJoinCampaignDialogHost />
        </ApplicationErrorBoundary>
        <ApplicationErrorBoundary context="update-notification" fallback={null}>
          <UpdateNotification />
        </ApplicationErrorBoundary>
      </CustomThemeProvider>
    </>
  );
}

const CustomThemeProvider: FC<PropsWithChildren<{ settings: ApplicationSettings | null }>> = ({
  settings,
  children,
}) => {
  const [theme, setTheme] = useState<Theme | null>(null);

  useEffect(() => {
    if (settings) {
      const theme = createAppTheme(settings);
      setTheme(theme);
    }
  }, [settings]);

  useEffect(() => {
    if (theme) {
      hideAppBootSpinner();
    }
  }, [theme]);

  if (!theme) {
    return null;
  }

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default App;
